"use client";

import Link from "next/link";
import IconSprite from "../common/IconSprite";
import { ROUTES } from "@/config/routes";
import { HomeSection } from "./HomeSection";
import { cn } from "@/lib/utils";
import { StaggerContainer, StaggerItem } from "@/components/animations";

interface MenuItem {
  id: string;
  title: string;
  href: string;
  iconId: string;
}

const menuItems: MenuItem[] = [
  {
    id: "equipo-nuevo",
    title: "EQUIPO NUEVO",
    href: ROUTES.industries.root,
    iconId: "ic_equipoNuevo"
  },
  {
    id: "equipo-usado",
    title: "EQUIPO USADO",
    href: ROUTES.usedEquipments.root,
    iconId: "ic_equipoUsado"
  },
  {
    id: "equipo-renta",
    title: "EQUIPO EN RENTA",
    href: ROUTES.rentals.root,
    iconId: "ic_equipoRenta"
  },
  {
    id: "tecnologia-mineria",
    title: "TECNOLOGÍA PARA MINERÍA",
    href: ROUTES.technologySolutions.details("tecnologia-para-mineria"),
    iconId: "ic_Tecnologia"
  },
  {
    id: "tecnologia-construccion",
    title: "TECNOLOGÍA PARA CONSTRUCCIÓN",
    href: ROUTES.external.constructionTechnology,
    iconId: "ic_Construccion"
  }
];

interface MenuItemProps {
  item: MenuItem;
  className?: string;
}

const MenuItem = ({ item, className = "" }: MenuItemProps) => {
  const isExternal = item.href.startsWith('http');

  return (
    <Link
      href={item.href}
      {...(isExternal && { target: "_blank", rel: "noopener noreferrer" })}
      className={cn(
        "group flex flex-col items-center bg-light02 rounded-lg overflow-hidden",
        "border border-transparent transition-all duration-500",
        "hover:scale-105 hover:-translate-y-1",
        "hover:border-matco hover:shadow-[0px_0px_8px_0px_rgba(255,210,1,0.25)]",
        "w-full sm:w-auto",
        className
      )}
    >
      <div className={cn(
        "w-full flex items-center justify-center",
        "pt-4 px-4 sm:pt-5 sm:px-5 md:pt-6 md:px-6 lg:pt-8 lg:px-8 ",
        "h-[100px] sm:h-[120px] md:h-[160px] lg:h-[180px]",
        "bg-light02 transition-colors duration-500"
      )}>
        <IconSprite
          id={item.iconId}
          className={cn(
            "w-20 h-20 md:w-32 md:h-32 lg:w-36 lg:h-36",
            "transition-all duration-500 group-hover:scale-105",
            "group-hover:[filter:drop-shadow(0_0_4px_rgba(255,210,1,0.5))]"
          )}
        />
      </div>

      <p className={cn(
        "w-full px-2 py-2 sm:px-3 sm:py-3 md:px-4 md:py-3 lg:px-5 lg:py-4",
        "text-center font-roboto font-medium text-dark02",
        "text-xl lg:text-2xl",
        "leading-none bg-light02",
        "transition-all duration-300",
        "flex items-center justify-center",
        "h-[40px] sm:h-[48px] md:h-[56px] lg:h-[64px] overflow-hidden"
      )}>
        <span className="sm:inline transition-all duration-300 group-hover:[text-shadow:0px_0px_4px_rgba(255,210,1,0.25)]">{item.title}</span>
      </p>
    </Link>
  );
};

export const SectionMenu = () => {

  return (
    <HomeSection
      title="SOLUCIONES EN MAQUINARIA Y TECNOLOGÍA PARA TU PROYECTO"
      className="bg-light01"
      centerTitle
    >
      {/* Desktop version with stagger animation */}
      <StaggerContainer staggerDelay={0.12} className="hidden md:flex md:flex-wrap md:justify-start md:gap-4 md:max-w-7xl md:mx-auto">
        {menuItems.map((item) => (
          <StaggerItem key={item.id} className="md:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-1rem)] xl:w-[calc(20%-0.8rem)]">
            <MenuItem item={item} />
          </StaggerItem>
        ))}
      </StaggerContainer>

      {/* Mobile version with stagger animation */}
      <div className="md:hidden flex flex-col gap-6">
        <StaggerContainer staggerDelay={0.1} className="flex flex-col gap-3 sm:gap-4 max-w-sm mx-auto w-full">
          {menuItems.map((item) => (
            <StaggerItem key={item.id}>
              <MenuItem item={item} />
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </HomeSection>
  );
};
